import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  interestClusterSubjectFamily,
  interestClusterTitle,
  interestMatchTier,
  relativeMatchPct
} from '../../utils/interestClusterDisplay';
import { getInterestClusterPopupCopy } from '../../data/interestClusterPopupCopy';
import { getInterestClusterTheme } from '../../utils/interestTheme';
import InterestClusterIllustration from './InterestClusterIllustration';
import CursorShyHorizontal from './CursorShyHorizontal';

const TIER_LABEL_KEYS = {
  best: 'interestCluster.tierBest',
  strong: 'interestCluster.tierStrong',
  good: 'interestCluster.tierGood',
  explore: 'interestCluster.tierExplore'
};

const ENTER_MS = 480;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Pop-up detail panel for a single interest cluster result.
 * @param {{
 *   cluster: { cluster_id?: number, theme?: string, label?: string, score?: number } | null,
 *   rank?: number,
 *   topScore?: number,
 *   isOpen: boolean,
 *   onClose: () => void
 * }} props
 */
export default function InterestClusterInfoModal({
  cluster,
  rank = 1,
  topScore = 0,
  isOpen,
  onClose
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const modalRef = useRef(null);
  const [entered, setEntered] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (!isOpen || !cluster) {
      setEntered(prefersReducedMotion());
      return undefined;
    }
    if (prefersReducedMotion()) {
      setEntered(true);
      return undefined;
    }
    setEntered(false);
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEntered(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, cluster?.cluster_id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !cluster) return null;

  const clusterId = Number(cluster.cluster_id);
  const family = interestClusterSubjectFamily(cluster);
  const theme = getInterestClusterTheme(cluster, family);
  const title = interestClusterTitle(cluster, t);
  const copy = getInterestClusterPopupCopy(clusterId, lang);
  const relativePct = relativeMatchPct(cluster.score, topScore);
  const tier = interestMatchTier(relativePct, rank);
  const tierLabel = t(TIER_LABEL_KEYS[tier]);
  const modalTitleId = `interest-cluster-modal-${clusterId}`;
  const motionClass = entered
    ? 'opacity-100 translate-y-0'
    : 'opacity-0 translate-y-8 sm:translate-y-6';
  const backdropClass = entered ? 'bg-black/40' : 'bg-black/0';

  return (
    <div
      className={`fixed inset-0 z-[240] flex items-end justify-center sm:items-center p-3 sm:p-6 transition-colors ease-out ${backdropClass}`}
      style={{ transitionDuration: `${ENTER_MS}ms` }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div
        ref={modalRef}
        className={`relative w-full max-w-lg bg-surface rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-visible transition-all ease-out ${motionClass}`}
        style={{ transitionDuration: `${ENTER_MS}ms` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`relative shrink-0 overflow-visible bg-gradient-to-br ${theme.gradient} px-5 pt-5 pb-16 text-white rounded-t-2xl`}>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30 transition-colors"
            aria-label={t('interestCluster.popup.close')}
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${theme.badge}`}>
            {t('interestCluster.rankLabel', { rank })}
          </span>
          <h2 id={modalTitleId} className="mt-3 text-2xl font-bold leading-snug pr-8">
            {title}
          </h2>
          {copy?.tagline ? (
            <p className="mt-2 text-sm leading-relaxed text-white/90">{copy.tagline}</p>
          ) : null}
          <p className="mt-3 text-sm font-semibold text-white/95">{tierLabel}</p>
          <div
            className="absolute -left-5 -right-5 -bottom-10 h-[120px] overflow-visible pointer-events-none"
            aria-hidden
          >
            <CursorShyHorizontal boundsRef={modalRef} edgePadding={4}>
              <div className="drop-shadow-lg w-[min(100%,240px)] h-[120px] flex items-center justify-center">
                <InterestClusterIllustration cluster={cluster} className="max-h-[120px]" />
              </div>
            </CursorShyHorizontal>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-14 pb-5">
          {copy?.body ? (
            <p className="text-sm leading-relaxed text-ink-muted">{copy.body}</p>
          ) : null}
          {copy?.paths ? (
            <div className="mt-4 rounded-xl bg-surface-muted border border-edge px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle mb-1.5">
                {t('interestCluster.popup.pathsHeading')}
              </p>
              <p className="text-sm leading-relaxed text-ink">{copy.paths}</p>
            </div>
          ) : null}
        </div>

        <div className="shrink-0 overflow-hidden rounded-b-2xl border-t border-edge px-5 py-4">
          <Link
            to="/jupas-calculator"
            onClick={onClose}
            className="flex w-full items-center justify-center gap-1.5 rounded-full border-2 bg-surface px-4 py-2.5 text-sm font-medium transition-colors hover:bg-[color-mix(in_srgb,var(--cta-accent)_12%,rgb(var(--edvise-surface)))]"
            style={{
              borderColor: theme.accent,
              color: theme.ink,
              '--cta-accent': theme.accent
            }}
          >
            <span>{t('interestCluster.popup.jupasCta')}</span>
            <span aria-hidden className="text-base leading-none" style={{ color: theme.ink }}>
              →
            </span>
          </Link>
          <p className="mt-2 text-center text-xs text-ink-subtle">{t('interestCluster.popup.jupasHint')}</p>
        </div>
      </div>
    </div>
  );
}
